import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import RunPlanner from "@/components/scheduler/RunPlanner";
import { X, Loader2, Search, Route, MapPin, CheckSquare, Square } from "lucide-react";

const dstr = (iso) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—");

export default function RunCreateModal({ onClose, onCreated }) {
  const [step, setStep] = useState("pick");
  const [shifts, setShifts] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(() => new Set());

  useEffect(() => {
    api.get("/runs/available-shifts").then((r) => setShifts(r.data.items || []))
      .catch((e) => { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load shifts"); setShifts([]); });
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (shifts || []).filter((x) => !s || [x.title, x.task_legacy_id, x.postcode, x.city, x.work_type, x.status].some((v) => String(v || "").toLowerCase().includes(s)));
  }, [shifts, q]);

  const toggle = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allShown = filtered.length > 0 && filtered.every((x) => sel.has(x.shift_id));
  const toggleAll = () => setSel((p) => {
    const n = new Set(p);
    if (allShown) filtered.forEach((x) => n.delete(x.shift_id));
    else filtered.forEach((x) => n.add(x.shift_id));
    return n;
  });

  if (step === "plan") {
    return <RunPlanner shiftIds={[...sel]} onClose={() => setStep("pick")} onSaved={(id) => onCreated && onCreated(id)} />;
  }

  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/70 backdrop-blur-sm flex items-start justify-center p-3 overflow-y-auto" data-testid="run-create-modal">
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 sticky top-0 bg-white rounded-t-2xl z-10">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center"><Route className="w-5 h-5" /></span>
            <div>
              <h2 className="text-base font-bold text-slate-900">Add run — select shifts</h2>
              <p className="text-xs text-slate-500">Pick the shifts to include, then plan the days &amp; order</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700" data-testid="run-picker-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search job, postcode, town, status…" className="pl-9 h-9" data-testid="run-picker-search" />
            </div>
            <Button variant="outline" className="h-9 gap-1.5 shrink-0" onClick={toggleAll} disabled={!filtered.length} data-testid="run-picker-selectall">
              {allShown ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />} {allShown ? "Clear" : "All"}
            </Button>
          </div>

          {shifts === null ? (
            <div className="flex items-center gap-2 text-slate-500 py-8 justify-center"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /> Loading shifts…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-slate-500 py-10 border border-dashed border-slate-300 rounded-xl" data-testid="run-picker-empty">No shifts found.</div>
          ) : (
            <div className="max-h-[46vh] overflow-y-auto -mx-1 px-1 space-y-1.5">
              {filtered.map((s) => {
                const on = sel.has(s.shift_id);
                return (
                  <button key={s.shift_id} onClick={() => toggle(s.shift_id)} data-testid={`run-picker-shift-${s.shift_id}`}
                    className={`w-full text-left flex items-start gap-3 rounded-xl border p-3 transition-colors ${on ? "border-indigo-400 bg-indigo-50/60" : "border-slate-200 hover:border-indigo-200"}`}>
                    <span className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? "bg-indigo-600 border-indigo-600 text-white" : "border-slate-300"}`}>
                      {on && <CheckSquare className="w-3 h-3" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-900 truncate">{s.title}</span>
                        {s.task_legacy_id && <span className="text-[11px] font-mono text-slate-400">#{s.task_legacy_id}</span>}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                        <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {s.postcode || "no postcode"}{s.city ? ` · ${s.city}` : ""}</span>
                        <span>{dstr(s.date)}{s.start_time ? ` · ${s.start_time}` : ""}</span>
                        {s.status && <span className="text-slate-400">{s.status}</span>}
                      </div>
                      {s.run_id && <div className="text-[11px] text-amber-600 mt-0.5">Already in {s.run_name || s.run_id}</div>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-slate-200 sticky bottom-0 bg-white rounded-b-2xl">
          <span className="text-sm text-slate-500" data-testid="run-picker-count">{sel.size} selected</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={() => setStep("plan")} disabled={!sel.size} className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="run-picker-continue">
              <Route className="w-4 h-4" /> Continue ({sel.size})
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
